import { Injectable } from '@angular/core';

const CLEANUP_COMPLETED_KEY = 'portal.legacy-service-worker-cleaned-v1';

@Injectable({ providedIn: 'root' })
export class LegacyServiceWorkerCleanupService {
    async cleanupOnce(): Promise<void> {
        if (this.cleanupWasCompleted()) {
            return;
        }

        const completed = await this.cleanup();
        if (!completed) {
            return;
        }

        try {
            localStorage.setItem(CLEANUP_COMPLETED_KEY, 'true');
        } catch {
            // A blocked localStorage must not prevent future cleanup attempts.
        }
    }

    async cleanup(): Promise<boolean> {
        try {
            await Promise.all([
                this.unregisterServiceWorkers(),
                this.clearAngularServiceWorkerCaches(),
            ]);
            return true;
        } catch {
            return false;
        }
    }

    private cleanupWasCompleted(): boolean {
        try {
            return localStorage.getItem(CLEANUP_COMPLETED_KEY) === 'true';
        } catch {
            return false;
        }
    }

    private async unregisterServiceWorkers(): Promise<void> {
        if (!('serviceWorker' in navigator)) {
            return;
        }

        const registrations = await navigator.serviceWorker.getRegistrations();
        const angularRegistrations = registrations.filter((registration) => {
            const worker =
                registration.active ??
                registration.waiting ??
                registration.installing;
            return worker?.scriptURL.endsWith('/ngsw-worker.js');
        });
        const results = await Promise.all(
            angularRegistrations.map((registration) => registration.unregister())
        );

        if (results.some((unregistered) => !unregistered)) {
            throw new Error('A legacy service worker could not be unregistered.');
        }
    }

    private async clearAngularServiceWorkerCaches(): Promise<void> {
        if (!('caches' in globalThis)) {
            return;
        }

        const cacheKeys = await caches.keys();
        const results = await Promise.all(
            cacheKeys
                .filter((key) => key.startsWith('ngsw:'))
                .map((key) => caches.delete(key))
        );

        if (results.some((deleted) => !deleted)) {
            throw new Error('A legacy Angular service-worker cache could not be deleted.');
        }
    }
}
